// THE DECK CONTRACT — the 13-slide-type catalogue of the proposal-deck generator.
//
// Design is frozen (golden master = LCH01 deck v15; geometry lives in the renderer templates,
// never here and never in slide JSON): a slide carries CONTENT SLOTS only. This file is the
// single authority on which slots each slide type has, what shape each slot value takes and
// what character budget it must respect — imported by the client editor, the server routes
// (PATCH validation), the bound resolver and the PPTX renderer alike.
//
// Shared-module rules (like src/shared/i18n.ts): no pg, no React, no src/server imports.
// Labels and hints are ENGLISH source strings — pages translate them via their .de.ts/.fr.ts
// catalogues, never here.
//
// The deck_slides.slide_type CHECK constraint in schema.sql is this catalogue's SQL twin —
// extend both together.

// ────────────────────────────────────────────────────────────────────────────
// Slide types
// ────────────────────────────────────────────────────────────────────────────

export const DECK_SLIDE_TYPES = [
  'cover',
  'ausgangslage',
  'auftrag_split',
  'zielbild_timeline',
  'arbeitspakete_gantt',
  'arbeitsergebnisse_tabelle',
  'traceability_mesh',
  'referenzen_karten',
  'team',
  'preis',
  'naechste_schritte_split',
  'bild_split',
  'anhang_divider',
  'anhang_register',
  'anhang_arbeitsergebnisse',
  'back_cover',
] as const;

export type DeckSlideType = (typeof DECK_SLIDE_TYPES)[number];

export const isDeckSlideType = (v: unknown): v is DeckSlideType =>
  typeof v === 'string' && (DECK_SLIDE_TYPES as readonly string[]).includes(v);

/**
 * The slides a fresh deck is created with, in v15 order (every slide individually
 * togglable, removable, and any type addable again — a divider appears twice: «Vorgehen»
 * before the plan, «Anhang» before the annex). `content` seeds the slots the master
 * carried as fixed wording (eyebrows, divider titles).
 */
export interface DefaultDeckSlide { type: DeckSlideType; content: Record<string, unknown> }
export const DEFAULT_DECK_SLIDES: readonly DefaultDeckSlide[] = [
  { type: 'cover', content: {} },
  { type: 'ausgangslage', content: { eyebrow: 'Ausgangslage' } },
  { type: 'auftrag_split', content: { eyebrow: 'Aufgabe', image: '/deck-assets/images/auftrag-leman.png' } },
  { type: 'zielbild_timeline', content: { eyebrow: 'Zielbild' } },
  { type: 'anhang_divider', content: { title: 'Vorgehen', image: '/deck-assets/images/vorgehen-fullbleed.jpg' } },
  { type: 'arbeitspakete_gantt', content: { eyebrow: 'Projektplan', title: 'Übersicht Arbeitspakete und -ergebnisse' } },
  { type: 'arbeitsergebnisse_tabelle', content: { eyebrow: 'Arbeitsergebnisse' } },
  { type: 'traceability_mesh', content: { eyebrow: 'Arbeitsweise' } },
  { type: 'referenzen_karten', content: { eyebrow: 'Referenzen' } },
  { type: 'team', content: { eyebrow: 'Team', title: 'Projektleitung und Projektorganisation.' } },
  { type: 'preis', content: { months: 6, days_per_month: 18, utilization_pct: 90, day_rate_chf: 1380 } },
  { type: 'naechste_schritte_split', content: { eyebrow: 'Die nächsten Schritte', image: '/deck-assets/images/schritte-vita.jpg' } },
  { type: 'anhang_divider', content: { title: 'Anhang', image: '/deck-assets/images/anhang-divider.jpg' } },
  { type: 'anhang_register', content: { eyebrow: 'Anhang A · Anforderungsregister' } },
  { type: 'anhang_arbeitsergebnisse', content: { eyebrow: 'Anhang B · Arbeitsergebnisregister' } },
  { type: 'back_cover', content: {} },
];
/** Type order of DEFAULT_DECK_SLIDES (kept for callers that only need the sequence). */
export const DEFAULT_DECK_ORDER: readonly DeckSlideType[] = DEFAULT_DECK_SLIDES.map((s) => s.type);

// ────────────────────────────────────────────────────────────────────────────
// Slot definitions
// ────────────────────────────────────────────────────────────────────────────

export type SlotKind =
  | 'text'   // single string, maxChars budget
  | 'lines'  // string[], maxItems lines of maxChars each
  | 'items'  // array of small objects, fields described by itemFields
  | 'image'  // '/deck-assets/…' path (choices, when present, are the curated suggestions)
  | 'ref'    // array of register/library identifiers (strings like 'REQ-012' or numeric ids)
  | 'choice' // single string out of a fixed choices list
  | 'int'    // non-negative integer (preis parameters)
  | 'number'; // non-negative finite number (preis parameters)

export interface SlotItemField {
  key: string;
  label: string;
  kind: 'text' | 'int' | 'number';
  maxChars?: number;
  /** For 'int'/'number': hard upper bound (a 150 % discount must not persist). */
  maxValue?: number;
}

export interface SlotDef {
  /**
   * Slot key in the slide's content JSON. When `dynamic` is true the key is a PREFIX and
   * content keys of the form `${key}${suffix}` belong to this slot (per-row overrides such
   * as 'kurzform:DEL-01' or 'takeaway_override:3').
   */
  key: string;
  label: string;
  kind: SlotKind;
  /** Hard character budget (per value; for 'lines' per line; overflow = error, never silent truncation). */
  maxChars?: number;
  /** For kind 'int'/'number': hard upper bound (overflow = error, like maxChars). */
  maxValue?: number;
  /** For 'lines' / 'items' / 'ref': maximum entries. */
  maxItems?: number;
  /** For 'lines' / 'items' / 'ref': required entry count — enforced only on a non-empty value (an empty array clears the slot). */
  minItems?: number;
  /** For kind 'items': the fields each entry carries. Unknown entry fields are dropped. */
  itemFields?: SlotItemField[];
  /** For kind 'choice': the allowed values. For kind 'image': curated suggestions (not enforced). */
  choices?: readonly string[];
  dynamic?: boolean;
  /**
   * Companion of an image slot (the key of that slot): the framing numbers `<image>_fx`,
   * `<image>_fy` (focal point, 0–100 %) and `<image>_zoom` (100–300 %). The editor renders
   * them inside the image picker, never as separate inputs.
   */
  frameOf?: string;
  hint: string;
}

/** The three framing companions every image slot carries (see SlotDef.frameOf). */
export function imageFrameSlots(imageKey: string): SlotDef[] {
  return [
    { key: `${imageKey}_fx`, label: 'Focal point x', kind: 'number', maxValue: 100, frameOf: imageKey,
      hint: 'Horizontal focal point of the crop, 0 = left edge, 100 = right edge (default 50).' },
    { key: `${imageKey}_fy`, label: 'Focal point y', kind: 'number', maxValue: 100, frameOf: imageKey,
      hint: 'Vertical focal point of the crop, 0 = top, 100 = bottom (default 50).' },
    { key: `${imageKey}_zoom`, label: 'Zoom', kind: 'number', maxValue: 300, frameOf: imageKey,
      hint: 'Zoom into the image in percent, 100 = the cover crop (default), up to 300.' },
  ];
}

/** Read an image slot's framing (defaults 50/50/100) from slide content. */
export interface ImageFrame { fx: number; fy: number; zoom: number }
export function imageFrameOf(content: Record<string, unknown>, imageKey: string): ImageFrame {
  const n = (k: string, d: number, lo: number, hi: number): number => {
    const v = content[k];
    return typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d;
  };
  return { fx: n(`${imageKey}_fx`, 50, 0, 100), fy: n(`${imageKey}_fy`, 50, 0, 100), zoom: n(`${imageKey}_zoom`, 100, 100, 300) };
}

/** The x where a split slide's image starts (text column left of it). */
export function splitXOf(content: Record<string, unknown>, fallback: number): number {
  const v = content.split_x;
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(900, Math.max(420, v)) : fallback;
}

export type BoundKind = 'cover' | 'gantt' | 'tabelle' | 'mesh' | 'referenzen' | 'team' | 'anhang' | 'anhangB';

export interface SlideTypeDef {
  type: DeckSlideType;
  name: string;
  slots: SlotDef[];
  /** Register/library data resolved live at preview/export — NEVER stored in slide JSON. */
  bound?: BoundKind[];
}

/** The curated slide imagery shipped with the app (public/deck-assets/images). */
export const DECK_IMAGE_CHOICES = [
  '/deck-assets/images/cover-hero.jpg',
  '/deck-assets/images/auftrag-leman.png',
  '/deck-assets/images/vorgehen-fullbleed.jpg',
  '/deck-assets/images/mesh-lch01.png',
  '/deck-assets/images/schritte-vita.jpg',
  '/deck-assets/images/anhang-divider.jpg',
  '/deck-assets/images/el-capitan.jpg',
  '/deck-assets/images/gold-und-silber.jpg',
  '/deck-assets/images/helm.jpg',
  '/deck-assets/images/leman.jpg',
  '/deck-assets/images/misty-shores.jpg',
  '/deck-assets/images/trees-of-life.jpg',
  '/deck-assets/images/velvet-mercury.jpg',
  '/deck-assets/images/vita.jpg',
] as const;

const eyebrow: SlotDef = {
  key: 'eyebrow', label: 'Eyebrow', kind: 'text', maxChars: 40,
  hint: 'Mono label top-left, uppercase, with the gold tick.',
};

/** Inline emphasis in prose slots: **bold** runs (the master sets them in Geist Black). */
const EMPHASIS_HINT = ' Wrap emphasised words in **double asterisks**.';

// ────────────────────────────────────────────────────────────────────────────
// The catalogue (budgets derived from LCH01 v15)
// ────────────────────────────────────────────────────────────────────────────

export const SLIDE_TYPE_CATALOG: readonly SlideTypeDef[] = [
  {
    type: 'cover',
    name: 'Cover',
    bound: ['cover'],
    slots: [
      { key: 'title', label: 'Title', kind: 'text', maxChars: 48,
        hint: 'Serif hero title, at most 2 lines of ~24 characters.' },
      { key: 'subtitle', label: 'Subtitle', kind: 'text', maxChars: 60,
        hint: 'Mono line under the title.' },
      { key: 'client_name', label: 'Client name', kind: 'text', maxChars: 12,
        hint: 'Lockup "{client} × NAVEN" — text fallback when the client record has no logo.' },
      { key: 'date_label', label: 'Date label', kind: 'text', maxChars: 40,
        hint: 'E.g. "Vorprojekt · September 2026".' },
      { key: 'hero_image', label: 'Hero image', kind: 'image', choices: DECK_IMAGE_CHOICES,
        hint: 'Full-bleed background photograph.' },
      ...imageFrameSlots('hero_image'),
    ],
  },
  {
    type: 'ausgangslage',
    name: 'Starting point',
    slots: [
      eyebrow,
      { key: 'paragraph1', label: 'First paragraph', kind: 'text', maxChars: 560,
        hint: 'Sans prose, 22.7px, two paragraphs fill the page (v15 page 2).' + EMPHASIS_HINT },
      { key: 'paragraph2', label: 'Second paragraph', kind: 'text', maxChars: 560,
        hint: 'Second paragraph, same voice.' + EMPHASIS_HINT },
    ],
  },
  {
    type: 'auftrag_split',
    name: 'Mandate (split)',
    slots: [
      eyebrow,
      { key: 'body', label: 'Body', kind: 'text', maxChars: 300,
        hint: 'Serif paragraph in the 500×260 text box — roughly 300 characters.' },
      { key: 'body2', label: 'Second paragraph', kind: 'text', maxChars: 460,
        hint: 'Sans paragraph under the serif one (v15: the value proposition).' + EMPHASIS_HINT },
      { key: 'image', label: 'Image', kind: 'image', choices: DECK_IMAGE_CHOICES,
        hint: 'Right-hand image, full height.' },
      ...imageFrameSlots('image'),
      { key: 'split_x', label: 'Split position', kind: 'int', maxValue: 900,
        hint: 'Where the image starts, in px of the 1280 width (v15: 755; 420–900).' },
    ],
  },
  {
    type: 'zielbild_timeline',
    name: 'Target picture & timeline',
    slots: [
      eyebrow,
      { key: 'title', label: 'Title', kind: 'text', maxChars: 56, hint: 'Serif slide title.' },
      { key: 'intro', label: 'Intro', kind: 'text', maxChars: 220, hint: 'Lead-in line under the title.' },
      { key: 'phases', label: 'Phase axis', kind: 'items', maxItems: 4,
        itemFields: [
          { key: 'label', label: 'Phase', kind: 'text', maxChars: 60 },
          { key: 'month', label: 'Month label', kind: 'text', maxChars: 18 },
        ],
        hint: 'Up to 4 phases over the cards, left to right (dot + mono month + phase name in the phase colour). The 4th ends the axis with the arrow.' },
      { key: 'items', label: 'Result cards', kind: 'items', maxItems: 6,
        itemFields: [
          { key: 'label', label: 'Title', kind: 'text', maxChars: 48 },
          { key: 'body', label: 'Body', kind: 'text', maxChars: 330 },
          { key: 'refs', label: 'DEL refs', kind: 'text', maxChars: 14 },
          { key: 'date', label: 'Date', kind: 'text', maxChars: 20 },
        ],
        hint: 'Six cards in the master geometry (card 2 gold-highlighted, card 6 the dark terminal card). refs/date fill the card foot.' + EMPHASIS_HINT },
      { key: 'terminal_milestones', label: 'Terminal milestones', kind: 'items', maxItems: 2,
        itemFields: [
          { key: 'label', label: 'Label', kind: 'text', maxChars: 20 },
          { key: 'date', label: 'Date', kind: 'text', maxChars: 14 },
        ],
        hint: 'Up to 2 gold-bar milestones inside the dark terminal card (e.g. «Abnahme GF 22.02.2027»).' },
    ],
  },
  {
    type: 'arbeitspakete_gantt',
    name: 'Work packages (Gantt)',
    bound: ['gantt'],
    slots: [
      eyebrow,
      { key: 'title', label: 'Title', kind: 'text', maxChars: 60, hint: 'Serif slide title.' },
      { key: 'intro', label: 'Intro', kind: 'text', maxChars: 160,
        hint: 'One sans line under the title (v15: «Die verschiedenen Arbeitspakete … münden in …»).' },
      { key: 'markers', label: 'Date markers', kind: 'items', maxItems: 3,
        itemFields: [
          { key: 'label', label: 'Label', kind: 'text', maxChars: 16 },
          { key: 'date', label: 'Date (YYYY-MM-DD)', kind: 'text', maxChars: 10 },
        ],
        hint: 'Dashed gold verticals with a mono label on the track (v15: «GF 22.02.» and «PrK 19./20.03.»).' },
      { key: 'phase_labels', label: 'Phase legend', kind: 'lines', maxItems: 4, maxChars: 22,
        hint: 'Up to 4 phase names for the colour legend.' },
    ],
  },
  {
    type: 'arbeitsergebnisse_tabelle',
    name: 'Deliverables table',
    bound: ['tabelle'],
    slots: [
      eyebrow,
      { key: 'title', label: 'Title', kind: 'text', maxChars: 60, hint: 'Serif slide title.' },
      { key: 'kurzform:', label: 'Acceptance summary override', kind: 'text', maxChars: 160, dynamic: true,
        hint: 'Per deliverable ("kurzform:DEL-01"); default is the first sentence of its definition of done.' },
      { key: 'subtitle:', label: 'Subtitle override', kind: 'text', maxChars: 90, dynamic: true,
        hint: 'Per deliverable ("subtitle:DEL-01"); default is the shortened description.' },
      { key: 'footer_note', label: 'Footer note', kind: 'text', maxChars: 110,
        hint: 'Mono footline.' },
    ],
  },
  {
    type: 'traceability_mesh',
    name: 'Traceability mesh',
    bound: ['mesh'],
    slots: [
      eyebrow,
      { key: 'title', label: 'Title', kind: 'text', maxChars: 80,
        hint: 'Serif title in the dark left panel.' },
      { key: 'shortname:', label: 'Short name override', kind: 'text', maxChars: 28, dynamic: true,
        hint: 'Per deliverable ("shortname:DEL-01"); the mesh row name, default is the register name cut to 28.' },
      { key: 'claims', label: 'Claims', kind: 'lines', maxItems: 3, maxChars: 160,
        hint: 'Up to 3 claims under the title.' },
      { key: 'highlight_reqs', label: 'Highlighted requirements', kind: 'ref',
        hint: 'REQ refs whose edges render gold.' },
      { key: 'legend', label: 'Legend', kind: 'text', maxChars: 160, hint: 'Mono legend line.' },
    ],
  },
  {
    type: 'referenzen_karten',
    name: 'Reference cards',
    bound: ['referenzen'],
    slots: [
      eyebrow,
      { key: 'title', label: 'Title', kind: 'text', maxChars: 80, hint: 'Serif slide title (v15 runs to 71 characters).' },
      { key: 'reference_ids', label: 'References', kind: 'ref', minItems: 3, maxItems: 3,
        hint: 'Exactly 3 mandate_references ids, left to right.' },
      { key: 'takeaway_override:', label: 'Takeaway override', kind: 'text', maxChars: 160, dynamic: true,
        hint: 'Per card ("takeaway_override:<id>"); replaces the seeded "Was hier zählt" line.' },
      { key: 'footer_line', label: 'Footer line', kind: 'text', maxChars: 120,
        hint: 'Serif closing line in Gold2.' },
    ],
  },
  {
    type: 'team',
    name: 'Team',
    bound: ['team'],
    slots: [
      eyebrow,
      { key: 'title', label: 'Title', kind: 'text', maxChars: 60, hint: 'Serif slide title.' },
      { key: 'parties', label: 'Third parties', kind: 'items', maxItems: 3,
        itemFields: [
          { key: 'label', label: 'Label', kind: 'text', maxChars: 28 },
          { key: 'name', label: 'Name', kind: 'text', maxChars: 32 },
          { key: 'body', label: 'Body', kind: 'text', maxChars: 240 },
        ],
        hint: 'Up to 3 third-party cards in the top row.' },
      { key: 'person_slots', label: 'People', kind: 'items', minItems: 2, maxItems: 2,
        itemFields: [
          { key: 'person_id', label: 'Person', kind: 'int' },
          { key: 'profile_context', label: 'Profile context', kind: 'text', maxChars: 40 },
        ],
        hint: 'Exactly 2 (person, profile-context) pairs — photo, role and bio come from the library.' },
    ],
  },
  {
    type: 'preis',
    name: 'Investment',
    slots: [
      { key: 'months', label: 'Months', kind: 'int', hint: 'Programme duration in months.' },
      { key: 'days_per_month', label: 'Days per month', kind: 'int',
        hint: 'Billable days per month — the utilised figure (18 at 90 %), already net of utilisation.' },
      { key: 'utilization_pct', label: 'Utilisation %', kind: 'number', maxValue: 100,
        hint: 'Display annotation on the days line (90 = 90 %). Does not enter the arithmetic.' },
      { key: 'day_rate_chf', label: 'Day rate (CHF)', kind: 'number', hint: 'Standard daily rate.' },
      { key: 'discounts', label: 'Discounts', kind: 'items', maxItems: 3,
        itemFields: [
          { key: 'label', label: 'Label', kind: 'text', maxChars: 44 },
          { key: 'pct', label: 'Percent', kind: 'number', maxValue: 100 },
        ],
        hint: 'Sequential discounts, each applied to the already-discounted value.' },
      { key: 'budget_label', label: 'Budget label', kind: 'text', maxChars: 140,
        hint: 'Mono line naming the budget frame.' },
      { key: 'intro', label: 'Intro', kind: 'text', maxChars: 200, hint: 'Lead-in paragraph (left column).' },
      { key: 'body2', label: 'Second paragraph', kind: 'text', maxChars: 380,
        hint: 'Left column, second paragraph (v15: what the two conditions are).' },
      { key: 'body3', label: 'Third paragraph', kind: 'text', maxChars: 380,
        hint: 'Left column, third paragraph (v15: payment terms).' },
    ],
  },
  {
    type: 'naechste_schritte_split',
    name: 'Next steps (split)',
    slots: [
      eyebrow,
      { key: 'title', label: 'Title', kind: 'text', maxChars: 40, hint: 'Serif slide title.' },
      { key: 'steps', label: 'Steps', kind: 'items', minItems: 3, maxItems: 3,
        itemFields: [
          { key: 'title', label: 'Title', kind: 'text', maxChars: 36 },
          { key: 'body', label: 'Body', kind: 'text', maxChars: 160 },
        ],
        hint: 'Exactly 3 numbered steps.' },
      { key: 'footer_mono', label: 'Footer', kind: 'text', maxChars: 140, hint: 'Mono footline.' },
      { key: 'image', label: 'Image', kind: 'image', choices: DECK_IMAGE_CHOICES,
        hint: 'Right-hand image from x640.' },
      ...imageFrameSlots('image'),
      { key: 'split_x', label: 'Split position', kind: 'int', maxValue: 900,
        hint: 'Where the image starts, in px of the 1280 width (v15: 640; 420–900).' },
    ],
  },
  {
    type: 'bild_split',
    name: 'Split: text + image',
    slots: [
      eyebrow,
      { key: 'title', label: 'Title', kind: 'text', maxChars: 60, hint: 'Serif title on the paper half.' },
      { key: 'body', label: 'Body', kind: 'text', maxChars: 300,
        hint: 'Serif statement paragraph (27px), like the mandate slide.' },
      { key: 'body2', label: 'Second paragraph', kind: 'text', maxChars: 460,
        hint: 'Sans paragraph under it.' + EMPHASIS_HINT },
      { key: 'footer_mono', label: 'Footer', kind: 'text', maxChars: 140, hint: 'Mono footline.' },
      { key: 'image', label: 'Image', kind: 'image', choices: DECK_IMAGE_CHOICES,
        hint: 'The photograph, full height on its side of the split.' },
      ...imageFrameSlots('image'),
      { key: 'image_side', label: 'Image side', kind: 'choice', choices: ['right', 'left'],
        hint: 'Which half the image takes (default right).' },
      { key: 'split_x', label: 'Split position', kind: 'int', maxValue: 900,
        hint: 'Width of the split edge from the left, px of 1280 (default 640; 420–900).' },
    ],
  },
  {
    type: 'anhang_divider',
    name: 'Section divider',
    slots: [
      { key: 'label', label: 'Label', kind: 'text', maxChars: 40, hint: 'Optional mono eyebrow above the title.' },
      { key: 'title', label: 'Title', kind: 'text', maxChars: 60, hint: 'Serif divider title (88px).' },
      { key: 'body', label: 'Body', kind: 'text', maxChars: 220,
        hint: 'Optional sans lines under the title (v15 «Anhang»: what the register proves).' },
      { key: 'image', label: 'Image', kind: 'image', choices: DECK_IMAGE_CHOICES,
        hint: 'Full-bleed background photograph.' },
      ...imageFrameSlots('image'),
    ],
  },
  {
    type: 'anhang_register',
    name: 'Annex register',
    bound: ['anhang'],
    slots: [
      eyebrow,
      { key: 'page_titles', label: 'Page titles', kind: 'items', maxItems: 8,
        itemFields: [
          { key: 'category', label: 'Category', kind: 'text', maxChars: 40 },
          { key: 'title', label: 'Title', kind: 'text', maxChars: 60 },
        ],
        hint: 'Per requirement category, the serif title of its page(s).' },
      { key: 'descoped_note', label: 'Descoped note', kind: 'text', maxChars: 200,
        hint: 'Footline listing descoped requirements.' },
    ],
  },
  {
    type: 'anhang_arbeitsergebnisse',
    name: 'Annex deliverable register',
    bound: ['anhangB'],
    slots: [
      eyebrow,
      { key: 'page_titles', label: 'Page titles', kind: 'items', maxItems: 8,
        itemFields: [
          { key: 'milestone', label: 'Milestone / phase', kind: 'text', maxChars: 40 },
          { key: 'title', label: 'Title', kind: 'text', maxChars: 60 },
        ],
        hint: 'Per milestone (phase), the serif page title; default is the milestone name with a full stop.' },
      { key: 'due_note:', label: 'Due-date note', kind: 'text', maxChars: 40, dynamic: true,
        hint: 'Per deliverable ("due_note:DEL-01"), appended to the TERMIN line — e.g. «in Klärung (Q-011)».' },
    ],
  },
  {
    type: 'back_cover',
    name: 'Back cover',
    slots: [
      { key: 'tagline', label: 'Tagline', kind: 'text', maxChars: 60,
        hint: 'Sans line under the wordmark (v15: «Zürich · Lausanne · Remote»).' },
      { key: 'lines', label: 'Contact lines', kind: 'lines', maxItems: 8, maxChars: 60,
        hint: 'Up to 8 mono contact/company lines; an empty line leaves a gap.' },
      { key: 'note', label: 'Note', kind: 'text', maxChars: 120, hint: 'Closing line.' },
    ],
  },
];

export const SLIDE_TYPE_BY_ID: Readonly<Record<DeckSlideType, SlideTypeDef>> = Object.fromEntries(
  SLIDE_TYPE_CATALOG.map((def) => [def.type, def]),
) as Record<DeckSlideType, SlideTypeDef>;

/** The SlotDef a content key belongs to (dynamic prefixes included), or null when unknown. */
export function findSlotDef(type: DeckSlideType, contentKey: string): SlotDef | null {
  for (const def of SLIDE_TYPE_BY_ID[type].slots) {
    if (def.dynamic ? contentKey.startsWith(def.key) && contentKey.length > def.key.length : def.key === contentKey) {
      return def;
    }
  }
  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// Slide-content interfaces (what deck_slides.content holds per type)
// ────────────────────────────────────────────────────────────────────────────

export interface CoverContent extends ImageFramed {
  title?: string;
  subtitle?: string;
  client_name?: string;
  date_label?: string;
  hero_image?: string;
}

export interface AusgangslageContent {
  eyebrow?: string;
  paragraph1?: string;
  paragraph2?: string;
}

/** Framing companions every image slot may carry (see imageFrameSlots). */
export interface ImageFramed {
  [frame: `${string}_fx`]: number | undefined;
  [frame: `${string}_fy`]: number | undefined;
  [frame: `${string}_zoom`]: number | undefined;
}

export interface AuftragSplitContent extends ImageFramed {
  eyebrow?: string;
  body?: string;
  body2?: string;
  image?: string;
  split_x?: number;
}

export interface BildSplitContent extends ImageFramed {
  eyebrow?: string;
  title?: string;
  body?: string;
  body2?: string;
  footer_mono?: string;
  image?: string;
  image_side?: 'right' | 'left';
  split_x?: number;
}

export interface ZielbildPhase { label?: string; month?: string }
export interface ZielbildItem { label?: string; body?: string; refs?: string; date?: string }
export interface ZielbildMilestone { label?: string; date?: string }
export interface ZielbildTimelineContent {
  eyebrow?: string;
  title?: string;
  intro?: string;
  phases?: ZielbildPhase[];
  items?: ZielbildItem[];
  terminal_milestones?: ZielbildMilestone[];
}

export interface GanttMarker { label?: string; date?: string }
export interface ArbeitspaketeGanttContent {
  eyebrow?: string;
  title?: string;
  intro?: string;
  markers?: GanttMarker[];
  phase_labels?: string[];
}

export interface ArbeitsergebnisseTabelleContent {
  eyebrow?: string;
  title?: string;
  footer_note?: string;
  /** Per-deliverable overrides, e.g. content['kurzform:DEL-01'] / content['subtitle:DEL-01']. */
  [override: `kurzform:${string}`]: string | undefined;
  [override: `subtitle:${string}`]: string | undefined;
}

export interface TraceabilityMeshContent {
  eyebrow?: string;
  title?: string;
  claims?: string[];
  highlight_reqs?: string[];
  legend?: string;
  /** Per-deliverable row-name override, e.g. content['shortname:DEL-01']. */
  [override: `shortname:${string}`]: string | undefined;
}

export interface ReferenzenKartenContent {
  eyebrow?: string;
  title?: string;
  reference_ids?: number[];
  footer_line?: string;
  /** Per-card override, e.g. content['takeaway_override:3'] (mandate_references.id). */
  [override: `takeaway_override:${string}`]: string | undefined;
}

export interface TeamParty { label?: string; name?: string; body?: string }
export interface TeamPersonSlot { person_id?: number; profile_context?: string }
export interface TeamContent {
  eyebrow?: string;
  title?: string;
  parties?: TeamParty[];
  person_slots?: TeamPersonSlot[];
}

export interface PreisDiscount { label?: string; pct?: number }
export interface PreisContent {
  months?: number;
  days_per_month?: number;
  utilization_pct?: number;
  day_rate_chf?: number;
  discounts?: PreisDiscount[];
  budget_label?: string;
  intro?: string;
  body2?: string;
  body3?: string;
}

export interface SchrittStep { title?: string; body?: string }
export interface NaechsteSchritteSplitContent extends ImageFramed {
  eyebrow?: string;
  title?: string;
  steps?: SchrittStep[];
  footer_mono?: string;
  image?: string;
  split_x?: number;
}

export interface AnhangDividerContent extends ImageFramed {
  label?: string;
  title?: string;
  body?: string;
  image?: string;
}

export interface AnhangPageTitle { category?: string; title?: string }
export interface AnhangRegisterContent {
  eyebrow?: string;
  page_titles?: AnhangPageTitle[];
  descoped_note?: string;
}

export interface AnhangBPageTitle { milestone?: string; title?: string }
export interface AnhangArbeitsergebnisseContent {
  eyebrow?: string;
  page_titles?: AnhangBPageTitle[];
  [override: `due_note:${string}`]: string | undefined;
}

export interface BackCoverContent {
  tagline?: string;
  lines?: string[];
  note?: string;
}

export interface SlideContentByType {
  cover: CoverContent;
  ausgangslage: AusgangslageContent;
  auftrag_split: AuftragSplitContent;
  zielbild_timeline: ZielbildTimelineContent;
  arbeitspakete_gantt: ArbeitspaketeGanttContent;
  arbeitsergebnisse_tabelle: ArbeitsergebnisseTabelleContent;
  traceability_mesh: TraceabilityMeshContent;
  referenzen_karten: ReferenzenKartenContent;
  team: TeamContent;
  preis: PreisContent;
  naechste_schritte_split: NaechsteSchritteSplitContent;
  bild_split: BildSplitContent;
  anhang_divider: AnhangDividerContent;
  anhang_register: AnhangRegisterContent;
  anhang_arbeitsergebnisse: AnhangArbeitsergebnisseContent;
  back_cover: BackCoverContent;
}

export type DeckSlideContent = SlideContentByType[DeckSlideType];

// ────────────────────────────────────────────────────────────────────────────
// Bound data — resolved live from the register/library, NEVER stored in slide JSON.
// Shapes are what GET /api/decks/:id/bound returns per slide and what both the HTML
// preview and the PPTX templates consume. Dates are 'YYYY-MM-DD' strings (pg convention).
// ────────────────────────────────────────────────────────────────────────────

/** arbeitspakete_gantt — one bar per deliverable; percentages are over the full time track. */
export interface GanttBoundRow {
  ref: string;
  /** Shortened to ≤44 characters by the resolver (v15 shows full names to that width). */
  name: string;
  startDate: string | null;
  dueDate: string | null;
  lane: number | null;
  /** Index into the 4 phase colours (light teal, gold, dark teal, black — the v15 order). */
  phaseIndex: number;
  leftPct: number;
  widthPct: number;
}
export interface GanttMarkerPlaced {
  label: string;
  /** Position over the time track, like the bars. */
  leftPct: number;
}
export interface GanttBound {
  rows: GanttBoundRow[];
  /** Equal-width month columns spanning min(start_date)–max(due_date). */
  months: { label: string }[];
  axisStart: string;
  axisEnd: string;
  /** The markers slot resolved onto the track (dates outside the axis are dropped). */
  markers: GanttMarkerPlaced[];
}

/** arbeitsergebnisse_tabelle — pre-paginated rows with the deckLogic placement applied. */
export interface TabelleBoundRow {
  ref: string;
  name: string;
  /** Small line under the name (subtitle:{ref} override or shortened description). */
  subtitle: string;
  /** Acceptance-criteria short form (kurzform:{ref} override or first DoD sentence). */
  kurzform: string;
  due: string | null;
  /** Vertical placement on the slide, from paginateTabelle. */
  y: number;
  height: number;
}
export interface TabelleBound {
  pages: TabelleBoundRow[][];
}

/** traceability_mesh — requirement groups, deliverable rows and the edges between them. */
export interface MeshGroup {
  category: string;
  /** Mono group header. */
  label: string;
  reqRefs: string[];
}
export interface MeshDelRow {
  ref: string;
  /** Shortened to ≤28 characters by the resolver. */
  name: string;
  due: string | null;
}
export interface MeshEdge {
  reqRef: string;
  delRef: string;
  /** True when the requirement is listed in the highlight_reqs slot — rendered gold. */
  highlight: boolean;
}
export interface MeshBound {
  groups: MeshGroup[];
  dels: MeshDelRow[];
  edges: MeshEdge[];
}

/** referenzen_karten — the 3 selected cards, takeaway_override already applied. */
export interface ReferenceCard {
  id: number;
  slug: string;
  contextLabel: string;
  claim: string;
  body: string;
  takeaway: string;
  moodImageKey: string | null;
  logoKey: string | null;
  logoRedacted: boolean;
}
export interface ReferenzenBound {
  cards: ReferenceCard[];
}

/** team — the 2 selected (person, profile) pairs, in slot order. */
export interface TeamBoundPerson {
  personId: number;
  name: string;
  photoKey: string | null;
  roleLabel: string;
  bio: string;
}
export interface TeamBound {
  persons: TeamBoundPerson[];
}

/** anhang_register — requirements grouped by category, paginated like the tabelle. */
export interface AnhangBoundRow {
  ref: string;
  statement: string;
  priority: 'must' | 'should' | 'could' | 'wont';
  /** Linked deliverable refs ("ERFÜLLT DURCH"). */
  delRefs: string[];
  y: number;
  height: number;
}
export interface AnhangBoundPage {
  category: string;
  /** From the page_titles slot, or the category name as fallback. */
  title: string;
  pageNo: number;
  pageCount: number;
  rows: AnhangBoundRow[];
}
export interface AnhangBound {
  pages: AnhangBoundPage[];
  /** Refs filtered out as descoped — surfaced in the descoped_note footline. */
  descopedRefs: string[];
}

/** anhang_arbeitsergebnisse — deliverable blocks (with their items) in two columns per page. */
export interface AnhangBItem { ref: string; title: string; lines: number }
export interface AnhangBBlock {
  ref: string;
  name: string;
  due: string | null;
  /** due_note:{ref} override, appended to the TERMIN line. */
  dueNote: string;
  items: AnhangBItem[];
  /** Placement inside its column (y from the page top; height of the whole block). */
  y: number;
  height: number;
}
export interface AnhangBPage {
  milestoneName: string;
  title: string;
  pageNo: number;
  pageCount: number;
  /** Two columns, left then right. */
  columns: [AnhangBBlock[], AnhangBBlock[]];
}
export interface AnhangBBound {
  pages: AnhangBPage[];
}

/** Cover lockup: the client half comes from the clients record (logo upload, name fallback). */
export interface CoverBound {
  client_id: number;
  client_name: string;
  /** TRUE when the client record carries an uploaded logo (served at /api/clients/:id/logo). */
  has_logo: boolean;
  /** Branding filename (data/uploads/branding) — the server renderer embeds it directly. */
  logo_key: string | null;
}

export interface DeckBoundByKind {
  cover: CoverBound;
  gantt: GanttBound;
  tabelle: TabelleBound;
  mesh: MeshBound;
  referenzen: ReferenzenBound;
  team: TeamBound;
  anhang: AnhangBound;
  anhangB: AnhangBBound;
}

// ────────────────────────────────────────────────────────────────────────────
// Content validation — pure, imported by client (editor) and server (PATCH → 400).
// ────────────────────────────────────────────────────────────────────────────

export interface SlotViolation {
  /** Content key, possibly with an index path ('items[2].body'). */
  slot: string;
  message: string;
}

export interface SlideContentValidation {
  ok: boolean;
  /**
   * The whitelisted content: unknown keys dropped (whitelist thinking, like the SOW's
   * normaliseVariables — a typo must never become a silent slide line), known values trimmed
   * and type-coerced. Over-budget values are kept UNTRUNCATED here and reported as
   * violations: persist `cleaned` only when `ok` is true.
   */
  cleaned: Record<string, unknown>;
  violations: SlotViolation[];
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function cleanString(
  value: unknown, slot: string, maxChars: number | undefined, violations: SlotViolation[],
): string | undefined {
  let s: string;
  if (typeof value === 'string') s = value;
  else if (typeof value === 'number' || typeof value === 'boolean') s = String(value);
  else {
    violations.push({ slot, message: 'must be text' });
    return undefined;
  }
  s = s.trim();
  if (maxChars !== undefined && s.length > maxChars) {
    violations.push({ slot, message: `exceeds the budget of ${maxChars} characters (${s.length})` });
  }
  return s;
}

function cleanNumber(
  value: unknown, slot: string, integer: boolean, violations: SlotViolation[], maxValue?: number,
): number | undefined {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof n !== 'number' || !Number.isFinite(n)) {
    violations.push({ slot, message: integer ? 'must be a whole number' : 'must be a number' });
    return undefined;
  }
  if (integer && !Number.isInteger(n)) {
    violations.push({ slot, message: 'must be a whole number' });
    return undefined;
  }
  if (n < 0) {
    violations.push({ slot, message: 'must not be negative' });
    return undefined;
  }
  if (maxValue !== undefined && n > maxValue) {
    violations.push({ slot, message: `must not exceed ${maxValue}` });
    return undefined;
  }
  return n;
}

function checkCount(def: SlotDef, slot: string, length: number, violations: SlotViolation[]): void {
  if (def.maxItems !== undefined && length > def.maxItems) {
    violations.push({ slot, message: `at most ${def.maxItems} entries allowed (${length})` });
  }
  // An empty array is an explicit "cleared" value and always allowed; the exact-count rule
  // ("exactly 3 references") bites only once the operator starts filling the slot.
  if (def.minItems !== undefined && length > 0 && length < def.minItems) {
    violations.push({ slot, message: `requires ${def.minItems} entries (${length})` });
  }
}

function cleanSlotValue(
  def: SlotDef, key: string, value: unknown, violations: SlotViolation[],
): unknown {
  switch (def.kind) {
    case 'text':
      return cleanString(value, key, def.maxChars, violations);

    case 'int':
      return cleanNumber(value, key, true, violations, def.maxValue);

    case 'number':
      return cleanNumber(value, key, false, violations, def.maxValue);

    case 'image': {
      const s = cleanString(value, key, undefined, violations);
      if (s === undefined) return undefined;
      if (s === '') return '';
      const normalised = s.startsWith('/') ? s : `/${s}`;
      const shipped = normalised.startsWith('/deck-assets/');
      const uploaded = /^\/deck-images\/[A-Za-z0-9._-]+$/.test(normalised);
      if ((!shipped && !uploaded) || normalised.includes('..')) {
        violations.push({ slot: key, message: 'must be a /deck-assets/… or /deck-images/… path' });
        return normalised;
      }
      return normalised;
    }

    case 'choice': {
      const s = cleanString(value, key, undefined, violations);
      if (s === undefined) return undefined;
      if (s !== '' && def.choices && !def.choices.includes(s)) {
        violations.push({ slot: key, message: 'is not one of the allowed values' });
      }
      return s;
    }

    case 'lines': {
      if (!Array.isArray(value)) {
        violations.push({ slot: key, message: 'must be a list of lines' });
        return undefined;
      }
      checkCount(def, key, value.length, violations);
      return value.map((line, i) => cleanString(line, `${key}[${i}]`, def.maxChars, violations) ?? '');
    }

    case 'ref': {
      if (!Array.isArray(value)) {
        violations.push({ slot: key, message: 'must be a list of references' });
        return undefined;
      }
      checkCount(def, key, value.length, violations);
      const out: (string | number)[] = [];
      value.forEach((entry, i) => {
        if (typeof entry === 'number' && Number.isFinite(entry)) out.push(entry);
        else if (typeof entry === 'string' && entry.trim() !== '') out.push(entry.trim());
        else violations.push({ slot: `${key}[${i}]`, message: 'must be a reference (id or ref string)' });
      });
      return out;
    }

    case 'items': {
      if (!Array.isArray(value)) {
        violations.push({ slot: key, message: 'must be a list of entries' });
        return undefined;
      }
      checkCount(def, key, value.length, violations);
      const fields = def.itemFields ?? [];
      return value.map((entry, i) => {
        if (!isPlainObject(entry)) {
          violations.push({ slot: `${key}[${i}]`, message: 'must be an entry object' });
          return {};
        }
        const cleanedEntry: Record<string, unknown> = {};
        for (const field of fields) {
          if (!(field.key in entry)) continue;
          const slot = `${key}[${i}].${field.key}`;
          const v = field.kind === 'text'
            ? cleanString(entry[field.key], slot, field.maxChars, violations)
            : cleanNumber(entry[field.key], slot, field.kind === 'int', violations, field.maxValue);
          if (v !== undefined) cleanedEntry[field.key] = v;
        }
        return cleanedEntry; // unknown entry fields dropped
      });
    }
  }
}

/**
 * Validate one slide's content against its type's slot catalogue. Pure.
 *
 * - Unknown keys are DROPPED from `cleaned` (whitelist, like normaliseVariables in
 *   sowRender.ts) — they are not violations.
 * - Character budgets are hard errors, never silent truncation.
 * - Absent slots are fine (a fresh slide starts as {}); exact-count slots enforce their
 *   count only on non-empty values.
 */
export function validateSlideContent(type: string, content: unknown): SlideContentValidation {
  if (!isDeckSlideType(type)) {
    return { ok: false, cleaned: {}, violations: [{ slot: 'slide_type', message: `unknown slide type "${type}"` }] };
  }
  if (content === undefined || content === null) {
    return { ok: true, cleaned: {}, violations: [] };
  }
  if (!isPlainObject(content)) {
    return { ok: false, cleaned: {}, violations: [{ slot: 'content', message: 'must be an object of slot values' }] };
  }

  const violations: SlotViolation[] = [];
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(content)) {
    const def = findSlotDef(type, key);
    if (!def) continue; // whitelist: unknown keys are dropped, silently
    const cleanedValue = cleanSlotValue(def, key, value, violations);
    if (cleanedValue !== undefined) cleaned[key] = cleanedValue;
  }
  return { ok: violations.length === 0, cleaned, violations };
}
